// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { type PositionsAnalysis } from '@shared/data-access/interfaces/redux/portfolio-state';
import {
  OperationType,
  normalizeOperationType,
} from '@shared/data-access/interfaces/operation-type';

/**
 * Helper function to get from/to ticker values based on operation type. Reads from
 * PositionsAnalysis which may have from_ticker/to_ticker preserved.
 */
export const getTickerValues = (position: PositionsAnalysis) => {
  const normalizedOperation = normalizeOperationType(position.operation);
  const positionAny = position as PositionsAnalysis & {
    from_ticker?: string;
    to_ticker?: string;
  };

  switch (normalizedOperation) {
    case OperationType.BUY:
      return {
        from_ticker: position.currency || null,
        to_ticker: positionAny.to_ticker || position.ticker,
      };
    case OperationType.SELL:
      return {
        from_ticker: positionAny.from_ticker || position.ticker,
        to_ticker: position.currency || null,
      };
    case OperationType.TRANSFER:
      return {
        from_ticker: positionAny.from_ticker || null,
        to_ticker: positionAny.to_ticker || position.ticker,
      };
    default:
      return { from_ticker: null, to_ticker: null };
  }
};

/**
 * Helper function to get from/to ISIN values based on operation type. Reads from
 * PositionsAnalysis which may have from_isin/to_isin preserved.
 */
export const getIsinValues = (position: PositionsAnalysis) => {
  const normalizedOperation = normalizeOperationType(position.operation);
  const positionAny = position as PositionsAnalysis & {
    from_isin?: string;
    to_isin?: string;
  };

  switch (normalizedOperation) {
    case OperationType.BUY:
      return {
        from_isin: position.currency || null,
        to_isin: positionAny.to_isin || position.isin || null,
      };
    case OperationType.SELL:
      return {
        from_isin: positionAny.from_isin || position.isin || null,
        to_isin: position.currency || null,
      };
    case OperationType.TRANSFER:
      return {
        from_isin: positionAny.from_isin || position.isin || null,
        to_isin: positionAny.to_isin || null,
      };
    default:
      return { from_isin: null, to_isin: null };
  }
};

const INVESTMENT_ACCOUNT = 'Investment Account';
const NUVARA_INVERSIS = 'Nuvara Inversis';

/** Helper function to check if position has account association */
export const hasAccountAssociation = (position: PositionsAnalysis): boolean => {
  return !!(
    position.account_id ||
    position.institution_id ||
    position.institution_name
  );
};

/**
 * Helper function to get from/to account values based on operation type. For SELL:
 * from account is always "Investment Account" For TRANSFER: from account is
 * "Investment Account", to account is "Nuvara Inversis" For BUY: from account is
 * "Cash Account", to account is "Nuvara Inversis"
 */
export const getAccountValues = (position: PositionsAnalysis) => {
  const normalizedOperation = normalizeOperationType(position.operation);
  switch (normalizedOperation) {
    case OperationType.BUY:
      return {
        from_account: 'Cash Account',
        to_account: NUVARA_INVERSIS,
      };
    case OperationType.SELL:
      return {
        from_account: INVESTMENT_ACCOUNT,
        to_account: 'Cash Account',
      };
    case OperationType.TRANSFER:
      return {
        from_account: INVESTMENT_ACCOUNT,
        to_account: NUVARA_INVERSIS,
      };
    default:
      return { from_account: null, to_account: null };
  }
};
